/**
 * API Service
 * Handles HTTP requests to the backend API
 * Auth tokens are extracted from the active Supabase session.
 */

import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
const SAFE_READ_RETRY_STATUSES = new Set([502, 503, 504]);
const SAFE_READ_MAX_ATTEMPTS = 2;
const SAFE_READ_RETRY_DELAY_MS = 750;
const SAFE_READ_TIMEOUT_MS = 15000;
const VENDOR_PRODUCTION_DOWNLOAD_PATTERN =
  /^\/(?:api\/v1\/)?vendor\/orders\/[1-9]\d*\/production_files\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/download$/i;

const normalizeApiEndpoint = (endpoint) => {
  const value = String(endpoint || '');
  if (!VENDOR_PRODUCTION_DOWNLOAD_PATTERN.test(value)) {
    throw new Error('Invalid supplier production-file download endpoint');
  }

  return value.startsWith('/api/v1/') ? value.slice('/api/v1'.length) : value;
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || data.error || 'Request failed');
    error.response = { data, status: response.status };
    throw error;
  }

  return { data, status: response.status };
};

const getAuthHeaders = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { 'Authorization': `Bearer ${session.access_token}` };
    }
  } catch (e) {
    console.warn('Failed to get Supabase session for API auth:', e.message);
  }
  return {};
};

const createAbortError = () => {
  const error = new Error('Request aborted');
  error.name = 'AbortError';
  return error;
};

const waitForRetry = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(createAbortError());
    return;
  }

  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  }, Math.max(0, delayMs));

  const handleAbort = () => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', handleAbort);
    reject(createAbortError());
  };

  signal?.addEventListener('abort', handleAbort, { once: true });
});

const fetchWithTimeout = async (url, options, timeoutMs, externalSignal) => {
  const controller = new AbortController();
  let timedOut = false;

  const handleExternalAbort = () => controller.abort();
  externalSignal?.addEventListener('abort', handleExternalAbort, { once: true });

  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    if (externalSignal?.aborted) {
      throw createAbortError();
    }

    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } catch (error) {
    if (timedOut) {
      const timeoutError = new Error('Request timed out');
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }

    throw error;
  } finally {
    clearTimeout(timeout);
    externalSignal?.removeEventListener('abort', handleExternalAbort);
  }
};

const isTransientSafeReadError = (error) => (
  error?.name === 'TimeoutError' ||
  (!error?.response && error?.name !== 'AbortError') ||
  SAFE_READ_RETRY_STATUSES.has(error?.response?.status)
);

const api = {
  get: async (endpoint, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });
    return handleResponse(response);
  },

  post: async (endpoint, body = {}, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      body: JSON.stringify(body),
      ...options,
    });
    return handleResponse(response);
  },

  getWithRetry: async (endpoint, options = {}) => {
    const {
      headers: optionHeaders = {},
      signal,
      maxAttempts = SAFE_READ_MAX_ATTEMPTS,
      retryDelayMs = SAFE_READ_RETRY_DELAY_MS,
      timeoutMs = SAFE_READ_TIMEOUT_MS,
      onRetry,
      ...fetchOptions
    } = options;
    const authHeaders = await getAuthHeaders();
    const boundedAttempts = Math.min(3, Math.max(1, Number(maxAttempts) || 1));

    for (let attempt = 1; attempt <= boundedAttempts; attempt += 1) {
      try {
        const response = await fetchWithTimeout(
          `${API_URL}${endpoint}`,
          {
            ...fetchOptions,
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
              ...optionHeaders,
            },
          },
          Math.max(1000, Number(timeoutMs) || SAFE_READ_TIMEOUT_MS),
          signal
        );

        return await handleResponse(response);
      } catch (error) {
        const shouldRetry = (
          attempt < boundedAttempts &&
          !signal?.aborted &&
          isTransientSafeReadError(error)
        );

        if (!shouldRetry) {
          throw error;
        }

        onRetry?.({
          attempt,
          nextAttempt: attempt + 1,
          status: error?.response?.status || null,
        });
        await waitForRetry(Number(retryDelayMs) * attempt || 0, signal);
      }
    }

    throw new Error('Safe read exhausted without a response');
  },

  upload: async (endpoint, formData, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const { headers: optionHeaders = {}, method = 'POST', ...fetchOptions } = options;
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      method,
      headers: {
        ...authHeaders,
        ...optionHeaders,
      },
      body: formData,
    });
    return handleResponse(response);
  },

  put: async (endpoint, body = {}, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      body: JSON.stringify(body),
      ...options,
    });
    return handleResponse(response);
  },

  patch: async (endpoint, body = {}, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      body: JSON.stringify(body),
      ...options,
    });
    return handleResponse(response);
  },

  delete: async (endpoint, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });
    return handleResponse(response);
  },

  download: async (endpoint, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const { headers: optionHeaders = {}, ...fetchOptions } = options;
    const response = await fetch(`${API_URL}${normalizeApiEndpoint(endpoint)}`, {
      ...fetchOptions,
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream',
        ...optionHeaders,
        ...authHeaders,
      },
    });

    if (!response.ok) {
      return handleResponse(response);
    }

    return response;
  },
};

export default api;
